import type { ConstructorGrid, GridPlacement, Pairing } from '../types'
import { findConflicts } from './constructor'

export type QualitySeverity = 'error' | 'warning' | 'info'

export type QualityJumpTarget = {
  row: number
  col: number
  placementId?: string | undefined
}

export type QualityCheckDetail = {
  id: string
  label: string
  jumpTo?: QualityJumpTarget | undefined
}

export type QualityCheck = {
  id: string
  title: string
  severity: QualitySeverity
  count: number
  description: string
  jumpTo?: QualityJumpTarget | undefined
  details?: QualityCheckDetail[] | undefined
}

export type QualityReport = {
  checks: QualityCheck[]
  totalIssues: number
  errorCount: number
  warningCount: number
  infoCount: number
  placementIssueCounts: Record<string, number>
  placementIssueLabels: Record<string, string[]>
}

const UNCHECKED_DETAIL_RATIO_THRESHOLD = 0.6

function normalizeClue(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, ' ')
}

function getPlacementLength(placement: GridPlacement): number {
  return placement.wordAText.replace(/\s/g, '').length
}

function getPlacementJumpTarget(grid: ConstructorGrid, placement: GridPlacement): QualityJumpTarget {
  const length = getPlacementLength(placement)
  for (let i = 0; i < length; i++) {
    const row = placement.direction === 'down' ? placement.row + i : placement.row
    const col = placement.direction === 'across' ? placement.col + i : placement.col
    if (row >= 0 && row < grid.height && col >= 0 && col < grid.width) {
      return { row, col, placementId: placement.id }
    }
  }

  return {
    row: Math.max(0, Math.min(grid.height - 1, placement.row)),
    col: Math.max(0, Math.min(grid.width - 1, placement.col)),
    placementId: placement.id
  }
}

function formatPlacementLabel(placement: GridPlacement): string {
  return `${placement.wordAText.replace(/\s/g, '')}/${placement.wordBText.replace(/\s/g, '')} (${placement.row + 1},${placement.col + 1})`
}

function addPlacementIssues(
  placementIssueCounts: Record<string, number>,
  placementIssueLabels: Record<string, string[]>,
  placementIds: string[],
  issueLabel: string,
  increment = 1
) {
  const uniqueIds = new Set(placementIds)
  for (const id of uniqueIds) {
    placementIssueCounts[id] = (placementIssueCounts[id] ?? 0) + increment
    const labels = placementIssueLabels[id]
    if (labels) {
      if (!labels.includes(issueLabel)) labels.push(issueLabel)
    } else {
      placementIssueLabels[id] = [issueLabel]
    }
  }
}

export function getQualityReport(
  grid: ConstructorGrid | null,
  pairings: Pairing[]
): QualityReport {
  if (!grid) {
    return {
      checks: [],
      totalIssues: 0,
      errorCount: 0,
      warningCount: 0,
      infoCount: 0,
      placementIssueCounts: {},
      placementIssueLabels: {}
    }
  }

  const pairingMap = new Map(pairings.map(p => [p.id, p]))
  const cellCoverage = new Map<string, number>()
  const blockedViolations: Array<{ row: number; col: number; placementId: string }> = []
  const missingCluePlacements: GridPlacement[] = []
  const unsavedTempPlacements: GridPlacement[] = []
  const placeholderPlacements: GridPlacement[] = []
  const outOfBoundsPlacements: GridPlacement[] = []
  const clueToPlacementIds = new Map<string, string[]>()
  const placementIssueCounts: Record<string, number> = {}
  const placementIssueLabels: Record<string, string[]> = {}

  for (const placement of grid.placements) {
    const length = getPlacementLength(placement)
    let hasInBoundsCell = false
    let hasOutOfBoundsCell = false

    const clueText = (pairingMap.get(placement.pairingId ?? '')?.clue?.text ?? '').trim()
    if (!clueText) {
      missingCluePlacements.push(placement)
    }

    if (placement.wordAText.includes('□') || placement.wordBText.includes('□')) {
      placeholderPlacements.push(placement)
    }

    const hasUnsavedTempA = Boolean(placement.isTempA && !placement.wordAText.includes('□'))
    const hasUnsavedTempB = Boolean(placement.isTempB && !placement.wordBText.includes('□'))
    if (hasUnsavedTempA || hasUnsavedTempB) {
      unsavedTempPlacements.push(placement)
    }

    for (let i = 0; i < length; i++) {
      const row = placement.direction === 'down' ? placement.row + i : placement.row
      const col = placement.direction === 'across' ? placement.col + i : placement.col
      if (row < 0 || row >= grid.height || col < 0 || col >= grid.width) {
        hasOutOfBoundsCell = true
        continue
      }

      hasInBoundsCell = true
      const cell = grid.cells[row]?.[col]
      if (cell?.isBlocked) {
        blockedViolations.push({ row, col, placementId: placement.id })
        continue
      }

      const key = `${row},${col}`
      cellCoverage.set(key, (cellCoverage.get(key) ?? 0) + 1)
    }

    if (hasOutOfBoundsCell) {
      outOfBoundsPlacements.push(placement)
    }

    if (clueText && hasInBoundsCell) {
      const key = normalizeClue(clueText)
      if (!clueToPlacementIds.has(key)) clueToPlacementIds.set(key, [])
      clueToPlacementIds.get(key)!.push(placement.id)
    }
  }

  const conflicts = findConflicts(grid).filter(c =>
    c.row >= 0 && c.row < grid.height && c.col >= 0 && c.col < grid.width
  )

  const uncheckedCells = Array.from(cellCoverage.entries())
    .filter(([, count]) => count === 1)
    .map(([key]) => {
      const [rowStr, colStr] = key.split(',')
      return { row: Number(rowStr), col: Number(colStr) }
    })
  const filledCellCount = cellCoverage.size
  const uncheckedRatio = filledCellCount === 0 ? 0 : uncheckedCells.length / filledCellCount

  const duplicateClueGroups = Array.from(clueToPlacementIds.entries())
    .filter(([, placementIds]) => placementIds.length > 1)
    .sort((a, b) => b[1].length - a[1].length)

  const duplicateClueCount = duplicateClueGroups.reduce(
    (sum, [, placementIds]) => sum + placementIds.length,
    0
  )

  const checks: QualityCheck[] = []

  if (conflicts.length > 0) {
    const details: QualityCheckDetail[] = conflicts.slice(0, 8).map((conflict, index) => ({
      id: `conflict-${index}`,
      label: `Cell (${conflict.row + 1},${conflict.col + 1})`,
      jumpTo: { row: conflict.row, col: conflict.col }
    }))

    if (conflicts.length > details.length) {
      details.push({
        id: 'conflict-more',
        label: `+${conflicts.length - details.length} more`
      })
    }

    addPlacementIssues(
      placementIssueCounts,
      placementIssueLabels,
      conflicts.flatMap(conflict => conflict.placementIds),
      'Letter conflicts'
    )

    checks.push({
      id: 'conflicts',
      title: 'Letter conflicts',
      severity: 'error',
      count: conflicts.length,
      description: 'Cells where overlapping entries disagree.',
      jumpTo: { row: conflicts[0]!.row, col: conflicts[0]!.col },
      details
    })
  }

  if (blockedViolations.length > 0) {
    const first = blockedViolations[0]!
    const details: QualityCheckDetail[] = blockedViolations.slice(0, 8).map((violation, index) => ({
      id: `blocked-${index}`,
      label: `Cell (${violation.row + 1},${violation.col + 1})`,
      jumpTo: violation
    }))
    if (blockedViolations.length > details.length) {
      details.push({
        id: 'blocked-more',
        label: `+${blockedViolations.length - details.length} more`
      })
    }

    addPlacementIssues(
      placementIssueCounts,
      placementIssueLabels,
      blockedViolations.map(violation => violation.placementId),
      'Placed through blocked cells'
    )

    checks.push({
      id: 'blocked-overlap',
      title: 'Placed through blocked cells',
      severity: 'error',
      count: blockedViolations.length,
      description: 'At least one entry crosses a black square.',
      jumpTo: first,
      details
    })
  }

  if (uncheckedCells.length > 0) {
    if (uncheckedRatio <= UNCHECKED_DETAIL_RATIO_THRESHOLD) {
      const details: QualityCheckDetail[] = uncheckedCells.slice(0, 10).map((cell, index) => ({
        id: `unchecked-${index}`,
        label: `Cell (${cell.row + 1},${cell.col + 1})`,
        jumpTo: cell
      }))
      if (uncheckedCells.length > details.length) {
        details.push({
          id: 'unchecked-more',
          label: `+${uncheckedCells.length - details.length} more`
        })
      }

      checks.push({
        id: 'unchecked',
        title: 'Unchecked cells',
        severity: 'warning',
        count: uncheckedCells.length,
        description: 'Cells confirmed by only one entry direction.',
        jumpTo: uncheckedCells[0],
        details
      })
    }
  }

  if (missingCluePlacements.length > 0) {
    const first = missingCluePlacements[0]!
    const details: QualityCheckDetail[] = missingCluePlacements.slice(0, 8).map((placement) => ({
      id: `missing-clue-${placement.id}`,
      label: formatPlacementLabel(placement),
      jumpTo: { row: placement.row, col: placement.col, placementId: placement.id }
    }))
    if (missingCluePlacements.length > details.length) {
      details.push({
        id: 'missing-clue-more',
        label: `+${missingCluePlacements.length - details.length} more`
      })
    }

    addPlacementIssues(
      placementIssueCounts,
      placementIssueLabels,
      missingCluePlacements.map(placement => placement.id),
      'Entries missing clues'
    )

    checks.push({
      id: 'missing-clues',
      title: 'Entries missing clues',
      severity: 'warning',
      count: missingCluePlacements.length,
      description: 'No clue text on the placement or linked pairing.',
      jumpTo: { row: first.row, col: first.col, placementId: first.id },
      details
    })
  }

  if (outOfBoundsPlacements.length > 0) {
    const first = outOfBoundsPlacements[0]!
    const details: QualityCheckDetail[] = outOfBoundsPlacements.slice(0, 8).map((placement) => ({
      id: `out-of-bounds-${placement.id}`,
      label: formatPlacementLabel(placement),
      jumpTo: getPlacementJumpTarget(grid, placement)
    }))
    if (outOfBoundsPlacements.length > details.length) {
      details.push({
        id: 'out-of-bounds-more',
        label: `+${outOfBoundsPlacements.length - details.length} more`
      })
    }

    addPlacementIssues(
      placementIssueCounts,
      placementIssueLabels,
      outOfBoundsPlacements.map(placement => placement.id),
      'Entries outside print grid'
    )

    checks.push({
      id: 'out-of-bounds',
      title: 'Entries outside print grid',
      severity: 'warning',
      count: outOfBoundsPlacements.length,
      description: 'One or more entries extend beyond the printable grid.',
      jumpTo: getPlacementJumpTarget(grid, first),
      details
    })
  }

  if (unsavedTempPlacements.length > 0) {
    const first = unsavedTempPlacements[0]!
    const details: QualityCheckDetail[] = unsavedTempPlacements.slice(0, 8).map((placement) => ({
      id: `unsaved-temp-${placement.id}`,
      label: formatPlacementLabel(placement),
      jumpTo: { row: placement.row, col: placement.col, placementId: placement.id }
    }))
    if (unsavedTempPlacements.length > details.length) {
      details.push({
        id: 'unsaved-temp-more',
        label: `+${unsavedTempPlacements.length - details.length} more`
      })
    }

    addPlacementIssues(
      placementIssueCounts,
      placementIssueLabels,
      unsavedTempPlacements.map(placement => placement.id),
      'Unsaved temporary words'
    )

    checks.push({
      id: 'unsaved-temp',
      title: 'Unsaved temporary words',
      severity: 'warning',
      count: unsavedTempPlacements.length,
      description: 'Entries include editable temp words that are not saved to lists yet.',
      jumpTo: { row: first.row, col: first.col, placementId: first.id },
      details
    })
  }

  if (placeholderPlacements.length > 0) {
    const first = placeholderPlacements[0]!
    const details: QualityCheckDetail[] = placeholderPlacements.slice(0, 8).map((placement) => ({
      id: `placeholder-${placement.id}`,
      label: formatPlacementLabel(placement),
      jumpTo: { row: placement.row, col: placement.col, placementId: placement.id }
    }))
    if (placeholderPlacements.length > details.length) {
      details.push({
        id: 'placeholder-more',
        label: `+${placeholderPlacements.length - details.length} more`
      })
    }

    addPlacementIssues(
      placementIssueCounts,
      placementIssueLabels,
      placeholderPlacements.map(placement => placement.id),
      'Placeholder letters remaining'
    )

    checks.push({
      id: 'placeholders',
      title: 'Placeholder letters remaining',
      severity: 'warning',
      count: placeholderPlacements.length,
      description: 'One or more entries still contain placeholder squares.',
      jumpTo: { row: first.row, col: first.col, placementId: first.id },
      details
    })
  }

  if (duplicateClueCount > 0) {
    const firstPlacementId = duplicateClueGroups[0]?.[1][0]
    const firstPlacement = firstPlacementId
      ? grid.placements.find(p => p.id === firstPlacementId)
      : undefined

    const details: QualityCheckDetail[] = []
    let hiddenDetailCount = 0
    const maxDetails = 16
    for (const [clue, placementIds] of duplicateClueGroups) {
      const displayClue = clue.length > 36 ? `${clue.slice(0, 36)}...` : clue
      for (const placementId of placementIds) {
        const placement = grid.placements.find(p => p.id === placementId)
        if (!placement) continue

        if (details.length < maxDetails) {
          details.push({
            id: `dup-clue-${placement.id}`,
            label: `"${displayClue}" -> ${formatPlacementLabel(placement)}`,
            jumpTo: { row: placement.row, col: placement.col, placementId: placement.id }
          })
        } else {
          hiddenDetailCount += 1
        }
      }
    }
    if (hiddenDetailCount > 0) {
      details.push({
        id: 'dup-clue-more',
        label: `+${hiddenDetailCount} more`
      })
    }

    addPlacementIssues(
      placementIssueCounts,
      placementIssueLabels,
      duplicateClueGroups.flatMap(([, placementIds]) => placementIds),
      'Duplicate clue text'
    )

    checks.push({
      id: 'duplicate-clues',
      title: 'Duplicate clue text',
      severity: 'info',
      count: duplicateClueCount,
      description: 'Multiple entries share the same normalized clue wording. Click each item to inspect all usages.',
      jumpTo: firstPlacement
        ? { row: firstPlacement.row, col: firstPlacement.col, placementId: firstPlacement.id }
        : undefined,
      details
    })
  }

  const totalIssues = checks.reduce((sum, check) => sum + check.count, 0)
  const errorCount = checks
    .filter(check => check.severity === 'error')
    .reduce((sum, check) => sum + check.count, 0)
  const warningCount = checks
    .filter(check => check.severity === 'warning')
    .reduce((sum, check) => sum + check.count, 0)
  const infoCount = checks
    .filter(check => check.severity === 'info')
    .reduce((sum, check) => sum + check.count, 0)

  return {
    checks,
    totalIssues,
    errorCount,
    warningCount,
    infoCount,
    placementIssueCounts,
    placementIssueLabels
  }
}
